import {formatAvmSpecParameter} from "../number-format";
import type {AvmSpecOutputParameter, AvmSpecParamCategory} from "../reva-spec";

/** A single formatted energy output row (one AVM output parameter), with DE/EN renderings. */
export interface EnergyOutputRow {
    parameter: string;
    type: string;
    exampleValue?: unknown;
    nameDe: string;
    nameEn: string;
    descriptionDe?: string | null;
    descriptionEn?: string | null;
    unitDe?: string | null;
    unitEn?: string | null;
    categories?: AvmSpecParamCategory[];
    value: unknown;
    valueDe: string;
    valueEn: string;
    low: unknown;
    lowDe: string;
    lowEn: string;
    high: unknown;
    highDe: string;
    highEn: string;
}

/**
 * A group of energy output rows plus its display metadata. Besides `title` and `position`, the
 * group is keyed by AVM parameter name, each mapping to an {@link EnergyOutputRow}.
 */
export interface EnergyOutputGroup {
    title: string;
    position: number;

    [parameter: string]: EnergyOutputRow | string | number;
}

/**
 * The keys of {@link EnergyOutputGroup} that are display metadata rather than a row. Kept next to the
 * interface because both have to change together: a consumer telling "has rows" from "empty group"
 * counts the keys that are NOT listed here, and {@link buildEnergyOutputRows} creates a group before
 * it knows whether any value belongs to it.
 */
export const ENERGY_GROUP_META_KEYS: ReadonlySet<string> = new Set(["title", "position"]);

/** Map of group key → {@link EnergyOutputGroup}. */
export type EnergyOutputGroups = Record<string, EnergyOutputGroup>;

/**
 * JSON output (listing the energy certificate was taken from) that has no single-value display.
 * Both names are skipped while the AVM rename energy_certificate_id → energy_certificate_offer rolls out (#19147).
 */
const SKIPPED_KEYS: ReadonlySet<string> = new Set(["energy_certificate_id", "energy_certificate_offer"]);

const ROW_GROUPS = [
    {
        key: "energy_certificate",
        regex: /energy_certificate|energy_|primary_energy/,
        title: "object.energy.output.energy_certificate",
        position: 0,
    },
    {key: "co2_emission", regex: /co2_/, title: "object.energy.output.co2_emission", position: 1},
    {
        key: "derived_object_properties",
        regex: /year_of_construction|property_|degree_modernization|^energetic_renovation_costs$/,
        title: "object.energy.output.derived_object_properties",
        position: 2,
    },
];

/** An AVM output parameter def plus its unformatted value/low/high, before display formatting. */
interface RawRow extends AvmSpecOutputParameter {
    value: unknown;
    low: unknown;
    high: unknown;
}

/** Formats a raw row for display: DE/EN renderings of `value`/`low`/`high`, metadata passed through unchanged. */
function formatRow(row: RawRow): EnergyOutputRow {
    const {
        parameter,
        type,
        exampleValue,
        nameDe,
        nameEn,
        descriptionDe,
        descriptionEn,
        unitDe,
        unitEn,
        categories,
        value,
        low,
        high,
    } = row;
    return {
        parameter,
        type,
        exampleValue,
        nameDe,
        nameEn,
        descriptionDe,
        descriptionEn,
        unitDe,
        unitEn,
        categories,
        value,
        valueDe: formatAvmSpecParameter("de", row, value, {omitUnit: true}),
        valueEn: formatAvmSpecParameter("en", row, value, {omitUnit: true}),

        low,
        lowDe: formatAvmSpecParameter("de", row, low, {omitUnit: true}),
        lowEn: formatAvmSpecParameter("en", row, low, {omitUnit: true}),

        high,
        highDe: formatAvmSpecParameter("de", row, high, {omitUnit: true}),
        highEn: formatAvmSpecParameter("en", row, high, {omitUnit: true}),
    };
}

/**
 * Groups an AVM energy-indication response into formatted output rows, ready for display.
 * Shared by the client object-energy tab and the reva-pdf energy section — reva-pdf only ever
 * reads the DE fields, but formatting both languages here means there's exactly one
 * implementation of the whole pipeline (grouping, filtering, formatting) to maintain.
 */
export function buildEnergyOutputRows(
    outputParameters: AvmSpecOutputParameter[],
    data: Record<string, unknown>,
): EnergyOutputGroups {
    const groups: EnergyOutputGroups = {};
    const baseParams = outputParameters.filter((p) => !/_high$|_hi$|_low$|_lo$/.test(p.parameter));
    for (const outputParameter of baseParams) {
        const key = outputParameter.parameter;
        if (key.startsWith("info_info") || SKIPPED_KEYS.has(key)) {
            continue;
        }
        const group = ROW_GROUPS.find((g) => g.regex.test(key));
        if (!group) continue;
        if (!groups[group.key]) groups[group.key] = {title: group.title, position: group.position};
        const value = data[key];
        if (!value) continue;
        groups[group.key][key] = formatRow({
            ...outputParameter,
            value,
            low: data[`${key}_low`] ?? data[`${key}_lo`],
            high: data[`${key}_high`] ?? data[`${key}_hi`],
        });
    }
    return groups;
}
